import type { Analytics } from "firebase/analytics";
import { firebaseConfig } from "./firebaseConfig";

/**
 * Google Analytics (Firebase) for the dashboard. On in production builds,
 * off in development unless VITE_ANALYTICS_ENABLED=true; VITE_ANALYTICS_ENABLED=false
 * turns it off anywhere. The SDK is loaded lazily after first paint, so it
 * never delays the app, and every call is a no-op when disabled/unsupported.
 * Page views are recorded automatically (GA4 tracks SPA history changes).
 */
const flag = import.meta.env.VITE_ANALYTICS_ENABLED;
export const analyticsEnabled = flag === "true" || (flag !== "false" && import.meta.env.PROD);

let ready: Promise<Analytics | null> | null = null;

function load(): Promise<Analytics | null> {
  ready ??= (async () => {
    if (!analyticsEnabled) return null;
    try {
      const [{ initializeApp }, { getAnalytics, isSupported }] = await Promise.all([
        import("firebase/app"),
        import("firebase/analytics"),
      ]);
      if (!(await isSupported())) return null; // e.g. cookies/IndexedDB blocked
      return getAnalytics(initializeApp(firebaseConfig));
    } catch (err) {
      console.warn("analytics unavailable", err);
      return null;
    }
  })();
  return ready;
}

/** Starts analytics once the browser is idle (call once at startup). */
export function initAnalytics(): void {
  if (!analyticsEnabled) return;
  const start = () => void load();
  if ("requestIdleCallback" in window) window.requestIdleCallback(start, { timeout: 3000 });
  else setTimeout(start, 1500);
}

/** Associates events with the signed-in staff member (internal id, never e-mail). */
export async function identify(user: { id: string; role: string } | null): Promise<void> {
  const a = await load();
  if (!a) return;
  const { setUserId, setUserProperties } = await import("firebase/analytics");
  setUserId(a, user?.id ?? null);
  setUserProperties(a, { staff_role: user?.role ?? null });
}

/** Custom event, e.g. track("course_published", { course_id }). */
export async function track(name: string, params?: Record<string, string | number | boolean>): Promise<void> {
  const a = await load();
  if (!a) return;
  const { logEvent } = await import("firebase/analytics");
  logEvent(a, name, params);
}
